import { HttpClient } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Preferences } from '@capacitor/preferences';
import { jwtDecode } from 'jwt-decode';
import { Observable, catchError, filter, first, firstValueFrom, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthenticationState } from '../enums/authentication-state.enum';
import { Tokens } from '../models/tokens.model';
import {
  CompleteYiviRegistrationDto,
  YiviAuthenticationResult,
  YiviRegistrationState,
  YiviStartSessionResponse,
} from '../models/yivi-authentication.model';
import { NotificationService } from './notification.service';
import { ProjectsService } from './projects.service';

const BASE_URL = `${environment.backendUrl}v1/authentication`;
const BASE_YIVI_URL = `${environment.backendUrl}v1/authentication-yivi`;

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  firstName = computed(() => {
    const t = this.decodedToken();
    return t && 'given_name' in t ? (t['given_name'] ?? null) : null;
  });

  lastName = computed(() => {
    const t = this.decodedToken();
    return t && 'family_name' in t ? (t['family_name'] ?? null) : null;
  });

  email = computed(() => {
    const t = this.decodedToken();
    return t && 'email' in t ? (t['email'] ?? null) : null;
  });

  /** The user's own display name from their identity token (Yivi/account), independent of any
   * organisation-managed person record. Used where the app must show who is logged in even when
   * the user has no person yet (identity decoupled from person, ILOPUMA-626). */
  fullName = computed<string | null>(() => {
    const t = this.decodedToken() as Record<string, unknown> | null;
    if (!t) return null;
    const name = typeof t['name'] === 'string' ? (t['name'] as string).trim() : '';
    if (name) return name;
    const given = typeof t['given_name'] === 'string' ? (t['given_name'] as string) : '';
    const family = typeof t['family_name'] === 'string' ? (t['family_name'] as string) : '';
    const composed = `${given} ${family}`.trim();
    return composed || null;
  });

  sub = computed(() => {
    const t = this.decodedToken();
    return t?.['sub'] ?? null;
  });

  // ────────────────────────────────────── signals ──────────────────────────────────────
  private refreshToken = signal<string | null>(null);
  private refreshing = signal(false);
  private refreshInProgress$: Observable<AuthenticationState> | null = null;

  private writableAuthenticationState = signal<AuthenticationState>(AuthenticationState.Pending);
  authenticationState = this.writableAuthenticationState.asReadonly();

  private writablePendingYiviRegistration = signal<YiviRegistrationState | null>(null);
  pendingYiviRegistration = this.writablePendingYiviRegistration.asReadonly();

  private writableToken = signal<string | null>(null);
  token = this.writableToken.asReadonly();

  private decodedToken = computed(() => {
    const t = this.token();
    return t ? jwtDecode(t) : null;
  });

  private notificationService = inject(NotificationService);
  private projectsService = inject(ProjectsService);

  /** Emits the first non‑pending authentication state – useful for guards & resolvers. */
  waitUntilReady$ = toObservable(this.authenticationState).pipe(
    filter((s) => s !== AuthenticationState.Pending),
    first(),
    shareReplay(1),
  );

  // start push notifications automatically after login
  startNotificationsOnLogin = effect(() => {
    if (this.authenticationState() === AuthenticationState.Authenticated) {
      this.notificationService.initialize();
    }
  });

  constructor(private httpClient: HttpClient) {
    void this.getTokenFromStorage();
  }

  login(username: string, password: string): Observable<AuthenticationState> {
    return this.httpClient.post<Tokens>(`${BASE_URL}/login`, { username, password }).pipe(
      switchMap((t) => this.setTokens(t)),
      map(() => AuthenticationState.Authenticated),
    );
  }

  startYiviAuthentication(): Observable<YiviStartSessionResponse> {
    return this.httpClient.post<YiviStartSessionResponse>(`${BASE_YIVI_URL}/start`, null);
  }

  completeYiviAuthentication(sessionToken: string): Observable<YiviAuthenticationResult> {
    return this.httpClient.get<YiviAuthenticationResult>(`${BASE_YIVI_URL}/result/${sessionToken}`);
  }

  completeYiviRegistration(dto: CompleteYiviRegistrationDto): Observable<YiviAuthenticationResult> {
    return this.httpClient.post<YiviAuthenticationResult>(`${BASE_YIVI_URL}/register`, dto);
  }

  async applyExternalAuthenticationResult(result: YiviAuthenticationResult): Promise<void> {
    if (result.status === 'authenticated' && result.tokens) {
      this.writablePendingYiviRegistration.set(null);
      await this.setTokens(result.tokens);
      return;
    }

    if (result.status === 'registration_required' && result.registration) {
      this.writablePendingYiviRegistration.set(result.registration);
      return;
    }

    throw new Error('Unexpected Yivi authentication result');
  }

  clearPendingYiviRegistration() {
    this.writablePendingYiviRegistration.set(null);
  }

  forgotPassword(username: string): Observable<boolean> {
    return this.httpClient.post<boolean>(`${BASE_URL}/forgot-password`, { username });
  }

  resetPassword(email: string, resetToken: string, newPassword: string): Observable<{ message: string }> {
    return this.httpClient.post<{ message: string }>(`${BASE_URL}/reset-password`, {
      email,
      resetToken,
      newPassword,
    });
  }

  async logout() {
    const notificationToken = await this.notificationService.getNotificationToken();

    await firstValueFrom(
      this.httpClient.post(`${BASE_URL}/logout`, { notificationToken: notificationToken.value }).pipe(
        catchError(() => of(null)), // never block logout flow
        tap(() => this.clearTokens()),
        tap(async () => {
          await Preferences.remove({ key: 'token' });
          await Preferences.remove({ key: 'refreshToken' });
          this.notificationService.unregister();
        }),
      ),
    );
  }

  refresh(): Observable<AuthenticationState> {
    if (!this.refreshToken() || this.authenticationState() !== AuthenticationState.Authenticated) {
      return of(this.authenticationState());
    }

    // If already refreshing → return the ongoing refresh observable
    if (this.refreshInProgress$) return this.refreshInProgress$;

    this.refreshing.set(true);

    this.refreshInProgress$ = this.httpClient.post<Tokens>(`${BASE_URL}/refresh`, this.refreshToken()).pipe(
      switchMap((t) => this.setTokens(t)),
      map(() => AuthenticationState.Authenticated),
      catchError(() => {
        this.clearTokens();
        return of(AuthenticationState.Unauthenticated);
      }),
      tap(() => {
        this.refreshing.set(false);
        this.refreshInProgress$ = null; // Reset so future refreshes can proceed
      }),
      shareReplay(1), // Ensure all subscribers share the result
    );

    return this.refreshInProgress$;
  }

  private async getTokenFromStorage() {
    const rt = await Preferences.get({ key: 'refreshToken' });
    if (rt?.value) this.refreshToken.set(rt.value);

    const tk = await Preferences.get({ key: 'token' });
    if (tk?.value) {
      this.writableToken.set(tk.value);

      console.info('Setting authentication state to authenticated');
      this.writableAuthenticationState.set(AuthenticationState.Authenticated);
    } else {
      console.info('Setting authentication state to unauthenticated');
      this.writableAuthenticationState.set(AuthenticationState.Unauthenticated);
    }
  }

  private async setTokens({ accessToken, refreshToken }: Tokens): Promise<void> {
    console.info('Setting tokens', { accessToken, refreshToken });
    this.writableToken.set(accessToken);
    this.refreshToken.set(refreshToken);
    console.info('Setting authentication state to authenticated');
    this.writableAuthenticationState.set(AuthenticationState.Authenticated);

    await Preferences.set({ key: 'token', value: accessToken });
    await Preferences.set({ key: 'refreshToken', value: refreshToken });
  }

  async fetchActiveToken() {
    await firstValueFrom(this.waitUntilReady$);

    const current = this.token();
    if (!current) {
      return null;
    }

    const expSeconds = jwtDecode(current).exp as number;

    if (expSeconds > Date.now() / 1000) {
      return current;
    }

    await firstValueFrom(this.refresh());
    return this.token();
  }

  private async clearTokens() {
    this.writableToken.set(null);
    this.refreshToken.set(null);
    this.writablePendingYiviRegistration.set(null);
    this.projectsService.clearCache();

    await Preferences.remove({ key: 'token' });
    await Preferences.remove({ key: 'refreshToken' });

    console.info('Setting authentication state to unauthenticated');
    this.writableAuthenticationState.set(AuthenticationState.Unauthenticated);
  }
}
